/**
 * 戦闘力サマリー / 新Qビュー / 関連チャート (BudgetBarChart, AceTeamsChart, OverallChart 等) で
 * PDG / ACE 集計に含めるメンバー判定を一元化する。`workspace.unified_members` の
 * `group_name` / `team_name` / `nick_name` を入力とする純粋関数。
 *
 * 仕様 (akita / 2026-05 #pi_pmo):
 * - **PDG**: `group=pi_pdg` かつ `team` が `pi-tet` / `pi_sret` のメンバー、加えて `ryan`
 *   (idt / pmot は除外)。
 * - **ACE**: `group=ACE` かつ `team` が `airCloset team` / `Mall team` / `Platform team` /
 *   `QA team` のメンバー、加えて `taro`
 *   (`backoffice team` および `所属なし` の他メンバーは除外)。
 *
 * team 名の表記揺れ (`pi-tet` ハイフン vs `pi_sret` アンダースコア, `airCloset team` の
 * 大文字小文字 / スペース) は unified_members の実値に合わせている。
 *
 * このモジュールは frontend / backend 双方の SSOT として参照する。重複定義は禁止。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PDG/ACE 集計対象判定ルール (戦闘力 / 新Q / KPI で共有)
 * @graph-connects INTERFACE_DESIGN_TEAM [references] IDT の team_name コード（組織コードの SSOT）
 * @graph-connects PMO_TEAM [references] PMOT の team_name コード（組織コードの SSOT）
 */
import { INTERFACE_DESIGN_TEAM, PMO_TEAM } from '@cortex/core-types';
/**
 * PDG（プロダクトグループ）の group_name。新体制 'プロダクトグループ' と旧体制 'pi_pdg' を併記。SSOT。
 * KPI のチーム絞り込み（kpi-team-filter）も、PDG 所属者だけに開くページ（CuttySark 賞）もここを参照する。
 * @graph-connects none
 */
export const PDG_GROUP_NAMES = ['プロダクトグループ', 'pi_pdg'];
/**
 * users.groupId が PDG（プロダクトグループ）かを判定する。チームは問わない（TET・SRE・IDT・PMOT 全員）。
 * 戦闘力の集計対象（isInPdgScope）とは別物で、こちらは「PDG グループの一員か」だけを見る。
 * @graph-connects PDG_GROUP_NAMES [references] PDG の group_name
 */
export function isPdgGroupMember(groupId) {
    return groupId !== null && groupId !== undefined && PDG_GROUP_NAMES.includes(groupId);
}
/**
 * PDG (group=pi_pdg) 集計対象の team_name セット。SSOT。
 * 他 app/SQL で PDG 集計対象を判定する場合もこの集合を import して使うこと (重複宣言禁止)。
 * @graph-connects none
 */
export const PDG_TEAMS = new Set(['pi-tet', 'pi_sret']);
/**
 * ACE サブチームの team_name リテラル。SSOT。
 * 白板振り分け等でサブチーム単位に判定する箇所も、リテラルを再宣言せずこれらを import すること
 * (重複宣言禁止)。表記揺れ (大文字小文字 / スペース) はここを唯一の真実として集約する。
 * @graph-connects none
 */
export const ACE_AIRCLOSET_TEAM = 'airCloset team';
/** @graph-connects none */
export const ACE_MALL_TEAM = 'Mall team';
/** @graph-connects none */
export const ACE_PLATFORM_TEAM = 'Platform team';
/** @graph-connects none */
export const ACE_QA_TEAM = 'QA team';
/**
 * ACE (group=ACE) 集計対象の team_name セット。SSOT。
 * 他 app/SQL で ACE 集計対象を判定する場合もこの集合を import して使うこと (重複宣言禁止)。
 * @graph-connects none
 */
export const ACE_TEAMS = new Set([
    ACE_AIRCLOSET_TEAM,
    ACE_MALL_TEAM,
    ACE_PLATFORM_TEAM,
    ACE_QA_TEAM,
]);
/**
 * group/team が条件外でも PDG 集計に含めるメンバー (nick_name lowercase)。SSOT。
 * @graph-connects none
 */
export const PDG_SPECIAL_NAMES = new Set(['ryan']);
/**
 * group/team が条件外でも ACE 集計に含めるメンバー (nick_name lowercase)。SSOT。
 * @graph-connects none
 */
export const ACE_SPECIAL_NAMES = new Set(['taro']);
/**
 * ACE サブチーム識別子の並び。SSOT。
 *
 * `KpiTeam`（`@cortex/domain-product-innovation-lab-types`）の部分集合だが、types 側が本
 * パッケージに依存している（`PMO_TEAM_NAME`）ため、逆向きの import は循環になる。よって
 * **識別子の正本はここに置き、types 側が re-export しつつ `satisfies readonly KpiTeam[]` で
 * KpiTeam との整合をコンパイル時に検証する**。
 * @graph-connects none
 */
export const ACE_SUB_TEAMS = ['aircloset', 'platform', 'mall', 'qa'];
/**
 * ACE サブチームごとに値を作り、`Record<AceSubTeam, T>` を返す。
 *
 * `Object.fromEntries(ACE_SUB_TEAMS.map(...))` はキーの型が `string` に落ちて `as` が要るが、
 * こちらは戻り値のオブジェクトリテラルが型検査されるため、`ACE_SUB_TEAMS` にチームが増えたら
 * **この 1 箇所がコンパイルエラーになる**（キャストで欠損キーが静かに通ることがない）。
 * チーム別内訳を組み立てる箇所（月次予算 / capacity 集計）はここを経由すること。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ACE サブチーム別の Record を網羅性を保証しつつ生成する
 * @graph-connects none
 */
export function mapAceTeams(build) {
    return {
        aircloset: build('aircloset'),
        platform: build('platform'),
        mall: build('mall'),
        qa: build('qa'),
    };
}
/**
 * ACE サブチームごとの メンバー nick_name (lowercase) セット。SSOT。
 *
 * fibo予算シートは member 名の行を持つが **team 列を持たない**ため、シート上の member 行が
 * どの ACE サブチームのものかは nick_name でしか判定できない。この対応表がその正本で、
 * `workspace.unified_members` の `group_name='ACE'` × `team_name` と同期を保つこと。
 * メンバーが入れ替わったらここに nick_name を足し引きすれば、シート側の集計も自動で追従する。
 *
 * 用途は 2 つ:
 *  - **PMO 充足率**: 分母から Mall 分を差し引く (`aceDevelopPointTarget − mall 合計`)。
 *  - **KPI 「開発pt (MTD)」/「リリース数 (MTD)」の ACE 各チームビュー**: そのチームの
 *    member 行だけを合算して チーム単位の目標線 (予算) を出す。
 *
 * 2026-09 時点の unified_members 実値は 44 名だが、fibo予算シート `_ACE` の「キャパ(pt)」区画に
 * 予算行を持つのは 27 名。予算行の無いメンバー (Mall の kai / katana / nicky 等) は合算に
 * 寄与しないが、チームの名簿を正本にするため列挙しておく (将来行が増えれば自動で乗る)。
 * 異動があった場合は移動先チームにだけ nick_name を置くこと (nick_name は前方一致ではなく
 * 完全一致で判定するため、同名が 2 チームに重複すると pt が二重計上される)。
 * @graph-connects none
 */
export const ACE_TEAM_MEMBER_NICKNAMES = {
    aircloset: new Set([
        'beat',
        'dan',
        'dee',
        'finn',
        'gin',
        'han',
        'hiro',
        'hot',
        'jason',
        'kenji',
        'kip',
        'lucci',
        'milkyway',
        'punna',
        'seiji',
        'shun',
        'slim',
        'smile',
        'suzuko',
        'thor',
    ]),
    platform: new Set(['esum', 'haku', 'ken', 'kuin', 'sabo', 'will', 'xman', 'yukito']),
    mall: new Set(['gozu', 'happy', 'hihi', 'kai', 'katana', 'luca', 'nicky']),
    qa: new Set(['akira', 'emi', 'fan', 'kaori', 'nunna', 'pinky', 'sora', 'tami', 'yuli']),
};
/**
 * PDG TE サブチーム識別子の並び。SSOT。
 *
 * `ACE_SUB_TEAMS` と同じ役割だが、**括りの作り方が異なる**。ACE サブチームは
 * `unified_members.team_name` が実在する (`airCloset team` 等) のに対し、TE サブチームは
 * Google Workspace の OU を分けず **`team_name='pi-tet'` のまま nick_name で 2 つに割る**
 * 運用上の括り。したがって `getTeamFilter` は team_name だけでは解決できず、TE の team_name
 * 条件に nick_name 条件を **重ねて** 適用する（`TeamFilter.nickNames`）。
 *
 * 識別子の正本をここに置き、types 側が re-export しつつ `satisfies readonly KpiTeam[]` で
 * KpiTeam との整合をコンパイル時に検証するのは `ACE_SUB_TEAMS` と同じ。
 *
 * 名前が指す担当領域（2026-10 の体制）:
 * - `growth`: GRG＋CMG 担当（獲得・単価）。NBT / CDT / BDT / FCT / CRM
 * - `experience`: CXG＋SCMG 担当（継続・SCM）。QDT / STT / CLT / SCMG / CST / PST / ADO
 * @graph-connects none
 */
export const TET_SUB_TEAMS = ['growth', 'experience'];
/**
 * PDG TE サブチームごとの メンバー nick_name (lowercase) セット。SSOT。
 *
 * TE は Workspace の OU を分けていない（全員 `team_name='pi-tet'`）ため、サブチームの所属は
 * **この表だけが正本**。KPI 側の `assignee` / `dev_leader` 列は `Melo` / `panda` / `billy` の
 * ように大小が混在するので、判定は必ず lowercase 同士で行うこと。
 *
 * 2026-10 の体制で TE 17 名のうち 14 名を 2 サブチームへ割り当てた。残る gen / miguel / tada は
 * **意図的に未所属**で、「PDG TE」には出るがどちらのサブチームにも出ない（サブチーム 2 つの
 * 合計は TE 合計と一致しない）。
 * 同じ nick_name を 2 チームに置くと pt が二重計上されるため、移動先にだけ置くこと。
 *
 * **この名簿が効く範囲**（異動時はここを足し引きすれば追従する）:
 * 信頼性 / バグ評価 / QA責任評価 / エンジニアボード の各一覧と、KPI 画面の週次pt・i-point・
 * pt進捗比較・月次トレンド。いずれも detail テーブルを直接引くため member 単位で絞れる。
 * エンジニアボードの**戦闘力サマリー**（`CombatPowerBlock` の TE サブチームスコープ）も
 * 本名簿で母集団を絞る。
 *
 * **ただしエンジニアボードの「健康度診断」パネルだけは別の名簿で動く**: Firestore
 * `combat_power_custom_views/pdg-te`（画面から編集できる最大 3 ビュー）を正本としており、
 * 本定数とは連動しない。TE の括りを表す名簿がリポジトリ内に 2 つある状態なので、
 * 異動時は両方を直すこと（どちらを正本にするかは未決。統合は別途検討）。
 *
 * KPI サマリー表と `kpi_daily_table` 由来のトレンドチャートも対象。同テーブルは
 * チーム別の集計列（`bug_rate_<team>` 等）を持つ形だが、`infra/generator/kpi/views.ts` が
 * **本定数を import して** サブチーム列を生成するため、名簿の正本は 1 つのまま。
 *
 * 照合先の列はテーブルごとに異なる（`dev_leader` / `assignee` / `nick_name` / Firestore
 * `users.nickName`）。**特に `dev_leader` は過去分に `tony` / `ryan` / `工藤和人` のような
 * 現行 nick_name と異なる値が残っている**（`infra/generator/kpi/views-detail.ts` の手動マッピング
 * 参照）。これらは TE には計上されるがサブチームのどちらにも載らないため、過去分の
 * サブチーム別バグ/リリース数は TE 合計より少なく出る。
 * @graph-connects none
 */
export const TET_TEAM_MEMBER_NICKNAMES = {
    growth: new Set(['billy', 'dhik', 'en', 'helles', 'lucky', 'maru', 'panda', 'stein']),
    experience: new Set(['jack', 'loki', 'melo', 'noah', 'ronnie', 'same']),
};
/**
 * PDG TE サブチームのリーダー nick_name (lowercase)。SSOT。
 *
 * PDG グループMTG の枠の担当（`mcp_access_lists/pidiv-management:owner_te_growth` /
 * `owner_te_experience`。[docs](../../../../../docs/mcp/pidiv-management.md)）と同じ人で、
 * 2026-10 時点は Growth=Dhik / Experience=Melo。
 *
 * **名簿（`TET_TEAM_MEMBER_NICKNAMES`）と同じ場所に置く。** 「誰がどのサブチームか」と
 * 「そのサブチームの誰がリーダーか」は同じ体制変更で一緒に動くので、離すと片方だけ直った
 * 状態（異動は反映されたのにリーダーが前任のまま）が生まれる。
 *
 * 用途: 要件定義アサインの Slack 通知が「サブリーダー」として mention する相手を引く
 * （`assign-input-mtg-guest.ts`）。リーダー自身も名簿に載っているため、担当エンジニアが
 * リーダー本人のときは呼び出し側で外すこと（自分を呼べという案内になる）。
 * @graph-connects none
 */
export const TET_SUB_TEAM_LEADER_NICKNAMES = {
    growth: 'dhik',
    experience: 'melo',
};
/**
 * nick_name から所属 PDG TE サブチームを引く。`TET_TEAM_MEMBER_NICKNAMES` を SSOT とする。
 *
 * どちらのサブチームにも属さない nick_name（**意図的に未所属の TE メンバー** gen / miguel /
 * tada、SRE、IDT、PMOT、ACE エンジニア、空セル等）は null を返す。`getAceTeamByMemberNickname`
 * の TE 版だが、ACE と違って `team_name` では割れない（TE は OU を分けていない）ため、
 * nick_name だけが判定材料になる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business nick_name から所属 PDG TE サブチーム (growth/experience) を解決
 * @graph-connects none
 */
export function getTetSubTeamByMemberNickname(name) {
    if (name == null)
        return null;
    const key = name.trim().toLowerCase();
    if (key === '')
        return null;
    for (const team of TET_SUB_TEAMS) {
        if (TET_TEAM_MEMBER_NICKNAMES[team].has(key))
            return team;
    }
    return null;
}
/**
 * nick_name から、その人の所属 PDG TE サブチームのリーダー nick_name (lowercase) を引く。
 * サブチームが引けなければ null。
 *
 * **リーダー本人を渡すとそのリーダー自身が返る**（リーダーも名簿に載っている）。「自分以外の
 * リーダー」が要る呼び出し側は返り値と入力を比べて外すこと——ここで null にすると、
 * 「リーダーが居ない」と「リーダーが自分」が区別できなくなる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business nick_name から所属 PDG TE サブチームのリーダー nick_name を解決
 * @graph-connects getTetSubTeamByMemberNickname [calls] 所属サブチームの解決
 */
export function getTetSubTeamLeaderNickname(name) {
    const team = getTetSubTeamByMemberNickname(name);
    return team === null ? null : TET_SUB_TEAM_LEADER_NICKNAMES[team];
}
/**
 * Mall team (`ACE_MALL_TEAM`) メンバーの nick_name (lowercase) セット。SSOT は
 * `ACE_TEAM_MEMBER_NICKNAMES.mall` で、こちらはその別名 (PMO 充足率が Mall 単独で参照するため)。
 * @graph-connects none
 */
export const MALL_MEMBER_NICKNAMES = ACE_TEAM_MEMBER_NICKNAMES.mall;
/**
 * IDT (designer) の team_name セット。SSOT。
 *
 * IDT designer は unified_members 上 `group=pi_pdg` だが `team=pi_idt` で、PDG エンジニア
 * (team=pi-tet/pi_sret) とは別集計。戦闘力の参照元も `【ID】pt plan` シートと異なるため
 * 専用判定を持つ。team 名は他グループと重複しないため team_name のみで判別できる。
 *
 * team_name の**リテラルは持たない**。組織コードは `@cortex/core-types` の
 * `INTERFACE_DESIGN_TEAM` が SSOT で、ここはその値を戦闘力集計用の集合に包むだけ
 * (同じ値を 2 か所に書くと、片方だけ直ったときに集計と権限判定がサイレントにずれる)。
 * @graph-connects none
 */
export const IDT_TEAMS = new Set([INTERFACE_DESIGN_TEAM]);
/**
 * PMO team の team_name。
 *
 * **リテラルは持たない。** 組織コードは `@cortex/core-types` の `PMO_TEAM` が SSOT で、ここは
 * 既存の import 名を保つための re-export（`INTERFACE_DESIGN_TEAM` → `IDT_TEAMS` と同じ関係）。
 * 同じ値を 2 か所に書くと、片方だけ直ったときに集計と権限判定がサイレントにずれる。
 * @graph-connects none
 */
export const PMO_TEAM_NAME = PMO_TEAM;
/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PDG (pi_pdg + pi-tet/pi_sret + ryan) 集計対象判定
 * @graph-connects none
 */
export function isInPdgScope(m) {
    if (PDG_SPECIAL_NAMES.has(m.name.toLowerCase()))
        return true;
    if (m.group !== 'pi_pdg')
        return false;
    return PDG_TEAMS.has(m.team);
}
/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ACE (ACE + 4 team + taro) 集計対象判定
 * @graph-connects none
 */
export function isInAceScope(m) {
    if (ACE_SPECIAL_NAMES.has(m.name.toLowerCase()))
        return true;
    if (m.group !== 'ACE')
        return false;
    return ACE_TEAMS.has(m.team);
}
/**
 * IDT designer (team=pi_idt) 判定。`getCombatScope` (PDG/ACE 集計) には含めず、
 * 戦闘力の参照元を `【ID】pt plan` シートへ切り替える用途で使う。team_name のみで判別する
 * (IDT は group=pi_pdg だが team=pi_idt で PDG エンジニアと区別される)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT designer (team=pi_idt) 判定
 * @graph-connects none
 */
export function isInIdtScope(m) {
    return IDT_TEAMS.has(m.team);
}
/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PDG/ACE/null の 3 値スコープラベル化
 * @graph-connects none
 */
export function getCombatScope(m) {
    if (isInPdgScope(m))
        return 'pdg';
    if (isInAceScope(m))
        return 'ace';
    return null;
}
/**
 * group_name を持たない文脈 (client の `User` 等) 向けに、team_name (+ 特例 nickname) だけで
 * PDG (pi-tet / pi_sret) または ACE (airCloset/Mall/Platform/QA) の **エンジニア** かを判定する。
 *
 * `getCombatScope` と異なり group を見ないが、PDG/ACE の team 名は他グループと重複しないため、
 * 「TET/ACE エンジニアにページを開放する」用途 (page-access 等) では十分に区別できる。
 * IDT designer (team=pi_idt / 所属なし) や PMO (pi_pmot) は team が条件外のため含まれない。
 * ryan(PDG) / taro(ACE) は team が条件外でも nickname で救済する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business team_name (+特例 nickname) だけで TET/ACE エンジニアかを判定 (group 不要)
 * @graph-connects none
 */
export function isCombatEngineerByTeam(input) {
    const name = (input.name ?? '').toLowerCase();
    if (PDG_SPECIAL_NAMES.has(name) || ACE_SPECIAL_NAMES.has(name))
        return true;
    const team = input.team ?? '';
    return PDG_TEAMS.has(team) || ACE_TEAMS.has(team);
}
/**
 * `isCombatEngineerByTeam` の **PDG 限定版**。team_name (+ 特例 nickname) だけで
 * 「PDG のエンジニア (team=pi-tet / pi_sret、加えて ryan)」かを判定する。
 *
 * ACE エンジニアを **含めない**のが `isCombatEngineerByTeam` との唯一の差。要件定義工程は
 * PDG 側にしか無く (fibo予算 `_PDG_v2` が「要件定義」行を持つのは PDG だけ)、要件定義pt を
 * 前提にした表示は ACE メンバーに出すと常に 0 になるため、PDG だけに絞る必要がある。
 *
 * group_name を持たない文脈 (client の `User` / my-page) 向けに team だけで判定する点は
 * `isCombatEngineerByTeam` と同じ。PDG/ACE の team 名は他グループと重複しないため、
 * IDT designer (pi_idt) や PMO (pi_pmot) は team が条件外で自然に外れる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business team_name (+特例 nickname) だけで PDG エンジニア (ACE を含めない) かを判定 (group 不要)
 * @graph-connects none
 */
export function isPdgEngineerByTeam(input) {
    if (PDG_SPECIAL_NAMES.has((input.name ?? '').toLowerCase()))
        return true;
    return PDG_TEAMS.has(input.team ?? '');
}
/**
 * nick_name が Mall team メンバーかを判定する。`MALL_MEMBER_NICKNAMES` を SSOT とし、
 * team 列を持たない fibo予算シートの member 行から Mall 分を抽出する用途に使う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business nick_name が Mall team メンバーかを判定
 * @graph-connects none
 */
export function isMallMemberNickname(name) {
    return name != null && MALL_MEMBER_NICKNAMES.has(name.toLowerCase());
}
/**
 * nick_name から所属 ACE サブチームを引く。`ACE_TEAM_MEMBER_NICKNAMES` を SSOT とし、
 * team 列を持たない fibo予算シートの member 行を チーム別に振り分ける用途に使う
 * (KPI 「開発pt (MTD)」の ACE 各チームビューの目標線)。どのチームにも属さない
 * nick_name (ACE 以外のメンバー、ラベル文字列、空セル等) は null を返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business nick_name から所属 ACE サブチーム (aircloset/platform/mall/qa) を解決
 * @graph-connects none
 */
export function getAceTeamByMemberNickname(name) {
    if (name == null)
        return null;
    const key = name.toLowerCase();
    for (const [team, nicknames] of Object.entries(ACE_TEAM_MEMBER_NICKNAMES)) {
        if (nicknames.has(key))
            return team;
    }
    return null;
}
//# sourceMappingURL=member-scope.js.map